/**
 * Reading and rewriting the external asset URIs of a glTF 2.0 document.
 *
 * Per the glTF 2.0 spec, a document references external files in exactly two
 * places: `buffers[].uri` and `images[].uri`. Every other reference
 * (accessors, bufferViews, materials, nodes, meshes) is an index into the
 * document itself. That makes the set of URIs handled here complete — there
 * is no third place a `.gltf` can hide a file reference.
 *
 * Kept as pure functions over a parsed document so the rewriting is testable
 * without a Babylon scene or a network.
 */

import type { GltfDocument } from "@/shared/domain/urdf";

/** Where in a glTF document a particular URI came from. */
export interface GltfUriSite {
  collection: "buffers" | "images";
  index: number;
  uri: string;
}

/**
 * Lists every `buffers[].uri` and `images[].uri` present in the document.
 *
 * Includes inline `data:` and absolute URIs; callers filter with
 * `isRelativeAssetUri` to find the ones that need resolving. Entries with no
 * `uri` at all are skipped — for `buffers` that means the GLB binary chunk,
 * and for `images` an embedded `bufferView`, neither of which is an external
 * reference.
 */
export function gltfUriSites(document: GltfDocument): GltfUriSite[] {
  const sites: GltfUriSite[] = [];

  for (const collection of ["buffers", "images"] as const) {
    const entries = document[collection];
    if (!Array.isArray(entries)) {
      continue;
    }
    entries.forEach((entry, index) => {
      const uri = entry?.uri;
      if (typeof uri === "string") {
        sites.push({ collection, index, uri });
      }
    });
  }

  return sites;
}

/**
 * Returns a copy of the document with the given URI sites replaced.
 *
 * Copies only the `buffers` and `images` arrays (and the entries being
 * rewritten) so the caller's document is left untouched; the rest of the
 * document — accessors, bufferViews, materials, nodes, meshes — is shared by
 * reference, since it is passed straight through to `JSON.stringify`.
 *
 * Sites not present in `replacements` keep their original URI, so an
 * already-absolute or inline URI survives a round-trip unchanged.
 */
export function withRewrittenUris(
  document: GltfDocument,
  replacements: GltfUriSite[],
): GltfDocument {
  if (replacements.length === 0) {
    return document;
  }

  const rewritten: GltfDocument = { ...document };

  for (const collection of ["buffers", "images"] as const) {
    const forCollection = replacements.filter(
      (replacement) => replacement.collection === collection,
    );
    if (forCollection.length === 0) {
      continue;
    }

    const entries = rewritten[collection];
    if (!Array.isArray(entries)) {
      continue;
    }

    const copy = [...entries];
    for (const replacement of forCollection) {
      const entry = copy[replacement.index];
      if (entry === undefined) {
        continue;
      }
      copy[replacement.index] = { ...entry, uri: replacement.uri };
    }
    rewritten[collection] = copy;
  }

  return rewritten;
}
